import React from "react";
import { ArrowUpRight } from "lucide-react";
import { BRAND } from "../../lib/constants";

const BRANDS = [
    {
        name: "Momia Healthcare Exports",
        tagline: "Pharmaceuticals · AYUSH · Hospital Projects",
        description:
            "Flagship export house for pharmaceutical formulations, AYUSH medicines, vaccines and complete turnkey hospital infrastructure.",
        logo: BRAND.logo,
        tone: "maroon",
        href: "#top",
        testid: "brand-momia",
        cta: "Core exports",
    },
    {
        name: "Adore Lifestyles",
        tagline: "Cosmetics · Personal Care · Wellness",
        description:
            "Our premium lifestyle and personal-care label — herbal skincare, haircare, massage oils, face wash, lotions and wellness ranges — designed for global private-label partners.",
        logo: BRAND.adoreLogo,
        tone: "gold",
        href: "#categories",
        testid: "brand-adore",
        cta: "Lifestyle & wellness",
    },
];

export default function Brands() {
    return (
        <section
            className="py-24 md:py-32 bg-[#FAF9F6] border-b border-stone-200"
            data-testid="brands-section"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
                    <div className="md:col-span-7">
                        <div className="overline mb-4">
                            <span className="inline-block w-8 h-px bg-stone-400 align-middle mr-3" />
                            Our Family of Brands
                        </div>
                        <h2 className="font-cabinet font-bold text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
                            Two houses,
                            <br />
                            <span className="italic font-normal text-stone-500">
                                one commitment to quality.
                            </span>
                        </h2>
                    </div>
                    <div className="md:col-span-5">
                        <p className="text-base md:text-lg text-stone-700 leading-relaxed">
                            Under a single export management, we run distinct
                            brand houses — serving regulated pharmaceutical
                            markets and the premium lifestyle & personal-care
                            segment in parallel.
                        </p>
                    </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                    {BRANDS.map((b) => (
                        <a
                            key={b.name}
                            href={b.href}
                            className={`group relative overflow-hidden border flex flex-col min-h-[420px] transition-all duration-500 ${
                                b.tone === "maroon"
                                    ? "bg-gradient-to-br from-[#7A1F2B] via-[#5F1822] to-[#3D0E16] text-white border-transparent hover:shadow-[0_20px_60px_-20px_rgba(122,31,43,0.5)]"
                                    : "bg-white text-ink border-stone-200 hover:border-[#B8860B] hover:shadow-[0_20px_60px_-20px_rgba(184,134,11,0.25)]"
                            }`}
                            data-testid={b.testid}
                        >
                            <div className="absolute inset-0 grain opacity-[0.08] pointer-events-none" />
                            <div className="relative flex-1 flex flex-col p-8 md:p-10">
                                <div className="flex items-start justify-between mb-8">
                                    <span
                                        className={`font-mono text-[10px] uppercase tracking-[0.22em] ${b.tone === "maroon" ? "text-white/70" : "text-stone-500"}`}
                                    >
                                        {b.cta}
                                    </span>
                                    <ArrowUpRight
                                        size={22}
                                        strokeWidth={1.5}
                                        className={`transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 ${b.tone === "maroon" ? "text-white/80" : "text-[#B8860B]"}`}
                                    />
                                </div>

                                <div
                                    className={`flex-1 flex items-center justify-center my-8 ${b.tone === "maroon" ? "" : "bg-[#FDFBF5]"} rounded-sm py-6`}
                                >
                                    <img
                                        src={b.logo}
                                        alt={`${b.name} logo`}
                                        className="h-40 md:h-48 w-auto object-contain transition-transform duration-700 group-hover:scale-105"
                                        style={
                                            b.tone === "maroon"
                                                ? {
                                                      filter: "brightness(0) invert(1)",
                                                  }
                                                : {}
                                        }
                                    />
                                </div>

                                <div>
                                    <h3
                                        className={`font-cabinet font-bold text-2xl md:text-3xl leading-tight tracking-tight ${b.tone === "gold" ? "text-ink" : ""}`}
                                    >
                                        {b.name}
                                    </h3>
                                    <div
                                        className={`mt-2 font-mono text-[11px] uppercase tracking-[0.2em] ${b.tone === "maroon" ? "text-white/70" : "text-stone-500"}`}
                                    >
                                        {b.tagline}
                                    </div>
                                    <p
                                        className={`mt-5 text-sm md:text-base leading-relaxed ${b.tone === "maroon" ? "text-white/90" : "text-stone-700"}`}
                                    >
                                        {b.description}
                                    </p>
                                </div>
                            </div>
                        </a>
                    ))}
                </div>
            </div>
        </section>
    );
}
